/**
 * UserflowsVisualPage (Iter 9.39c)
 *
 * Replaces the raw-markdown UserflowsViewer with a human-friendly sectioned
 * layout parsed from MAIN_USER_FLOWS.md + a live CHANGELOG panel.
 *
 * Every H2 in the markdown becomes a collapsible card; each occurrence of
 * `[INSERT VISUAL]` becomes an upload-visual placeholder.
 */
import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Map, Image as ImageIcon, ChevronDown, ChevronUp, Loader2, Clock,
  RefreshCw, ExternalLink, CheckCircle2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` });

export default function UserflowsVisualPage() {
  const [sections, setSections] = useState([]);
  const [changelog, setChangelog] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [md, cl] = await Promise.all([
          fetch(`${API}/admin/docs/MAIN_USER_FLOWS.md`, { headers: auth() }).then(r => r.ok ? r.json() : { content: "" }),
          fetch(`${API}/admin/product-changelog?limit=25`, { headers: auth() }).then(r => r.ok ? r.json() : { changelog: [] }),
        ]);
        setSections(parseSections(md.content || ""));
        setChangelog(cl.changelog || []);
      } finally { setLoading(false); }
    };
    load();
  }, []);

  const refreshChangelog = async () => {
    const r = await fetch(`${API}/admin/product-changelog/rebuild`, { method: "POST", headers: auth() });
    if (r.ok) {
      const d = await r.json();
      setChangelog(d.changelog || []);
    }
  };

  if (loading) return <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="userflows-visual-page">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <Map className="w-5 h-5" />
        <h1 className="text-xl font-bold">Userflows</h1>
        <Badge className="bg-purple-100 text-purple-700 border-0">{sections.length} screens</Badge>
        <div className="flex-1" />
        <a href="/admin/docs?file=MAIN_USER_FLOWS.md" className="text-xs text-purple-600 hover:underline inline-flex items-center" data-testid="userflows-open-md">
          Open raw .md <ExternalLink className="w-3 h-3 ml-0.5" />
        </a>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        Human-readable view of every screen, popup, analytic, and tier gate. Source of truth: <code>/app/memory/MAIN_USER_FLOWS.md</code>.
        Any <code>[INSERT VISUAL]</code> marker in the doc becomes an upload slot here.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
        {/* Sections */}
        <div className="space-y-2">
          {sections.map((s, i) => <Section key={i} section={s} />)}
        </div>

        {/* Changelog sidebar */}
        <aside className="space-y-3">
          <Card>
            <CardContent className="p-3 space-y-2">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <h3 className="font-semibold text-sm">Recent changes</h3>
                <button onClick={refreshChangelog} className="ml-auto text-gray-400 hover:text-gray-700" data-testid="changelog-refresh">
                  <RefreshCw className="w-3.5 h-3.5" />
                </button>
              </div>
              <p className="text-[11px] text-gray-500">Last 25 product changes from <code>db.product_changelog</code>. Nightly job appends new entries to <code>CHANGELOG.md</code>.</p>
              <ul className="space-y-1.5 max-h-[520px] overflow-y-auto">
                {changelog.length === 0 ? (
                  <li className="text-xs text-gray-400">No entries yet. Changes are auto-logged from admin mutations.</li>
                ) : changelog.map((c) => (
                  <li key={c.change_id} className="text-xs text-gray-700 border-l-2 border-purple-200 pl-2" data-testid={`changelog-${c.change_id}`}>
                    <div className="font-medium">{c.summary || c.change_type}</div>
                    <div className="text-[10px] text-gray-400">{(c.timestamp || "").slice(0, 19).replace("T", " ")}</div>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3">
              <h3 className="font-semibold text-sm mb-1">Tier gating legend</h3>
              <ul className="text-[11px] text-gray-600 space-y-0.5">
                <li><Badge className="bg-green-100 text-green-700 border-0 mr-1 text-[9px]">Free</Badge>All users</li>
                <li><Badge className="bg-blue-100 text-blue-700 border-0 mr-1 text-[9px]">Solo</Badge>Paid hosts</li>
                <li><Badge className="bg-purple-100 text-purple-700 border-0 mr-1 text-[9px]">Team</Badge>Multi-seat</li>
                <li><Badge className="bg-yellow-100 text-yellow-700 border-0 mr-1 text-[9px]">Luxe</Badge>Top tier</li>
                <li><Badge className="bg-gray-100 text-gray-700 border-0 mr-1 text-[9px]">Admin</Badge>Internal only</li>
              </ul>
            </CardContent>
          </Card>
        </aside>
      </div>
    </div>
  );
}

function Section({ section }) {
  const [open, setOpen] = useState(section.level <= 2);
  const needsVisual = section.body.includes("[INSERT VISUAL]");
  return (
    <Card className={`border ${needsVisual ? "border-amber-200" : ""}`} data-testid={`userflow-section-${section.slug}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-2 p-3 text-left hover:bg-gray-50">
        <span className={`text-xs font-mono text-gray-400`}>H{section.level}</span>
        <span className="font-medium text-sm flex-1">{section.title}</span>
        {needsVisual && <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px]"><ImageIcon className="w-3 h-3 mr-0.5" />Needs visual</Badge>}
        {!needsVisual && section.body.length > 50 && <CheckCircle2 className="w-4 h-4 text-green-500" />}
        {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      {open && (
        <CardContent className="pt-0 pb-3 px-3">
          <SectionBody body={section.body} />
        </CardContent>
      )}
    </Card>
  );
}

function SectionBody({ body }) {
  // Render the body as paragraphs. Replace [INSERT VISUAL] with an upload slot.
  const parts = body.split(/(\[INSERT VISUAL[^\]]*\])/g);
  return (
    <div className="space-y-2 text-sm text-gray-700">
      {parts.map((p, i) => {
        if (/^\[INSERT VISUAL/.test(p)) {
          return (
            <div key={i} className="border-2 border-dashed border-amber-200 rounded-lg p-6 bg-amber-50/40 text-center" data-testid="userflow-insert-visual">
              <ImageIcon className="w-6 h-6 text-amber-500 mx-auto mb-1" />
              <div className="text-xs text-amber-700 font-medium">Upload screenshot or diagram</div>
              <div className="text-[10px] text-amber-600/80 mt-0.5">Image upload in a follow-up iter; for now, edit the .md directly.</div>
            </div>
          );
        }
        return <p key={i} className="whitespace-pre-wrap leading-relaxed">{p}</p>;
      })}
    </div>
  );
}

// ─── Markdown parse: split by H1/H2/H3 headings ──────────────────
function parseSections(md) {
  const lines = md.split("\n");
  const out = [];
  let current = null;
  for (const raw of lines) {
    const m = raw.match(/^(#{1,3})\s+(.*)$/);
    if (m) {
      if (current) out.push(current);
      current = {
        level: m[1].length,
        title: m[2].trim(),
        slug: m[2].trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40),
        body: "",
      };
    } else if (current) {
      current.body += raw + "\n";
    }
  }
  if (current) out.push(current);
  return out.filter((s) => s.level <= 3);
}
